import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Button, LinearProgress, MenuItem, Popover, TextField } from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DayjsUtils from '@date-io/dayjs';
import {
  DocumentTemplateType,
  FileCategoryType,
  FileNode,
  IInsertionDocumentWithGraph,
} from '../../api-client/autogenerated';
import FileUploadDialog from './FileUploadDialog';
import { Clear, Publish } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectState } from '../../features/project/selectors';
import { documentTypeToUrl, generateUniqueId, getProjectUsers } from '../../scripts/utils';
import ManagePermissionsDialog, {
  ManagePermissionsDialogType,
} from '../design/ManagePermissionsDialog';
import InlineNotificationList from '../form-pages/user-groups/InlineNotificationList';
import Typography from '@material-ui/core/Typography';
import AssignedUserText from '../custom-components/AssignedUserText';
import { getCompaniesState } from '../../features/companies/selector';
import { getNavigationState } from '../../features/navigation/selectors';
import { CancelButton } from '../custom-components/CustomButtons';
import { allowNavigation, blockNavigation } from '../../features/navigation/actions';
import { addDocumentToPackage, insertDocument } from '../../models/api/documents';
import { getDocumentsByProjectIdAndType } from '../../models/api/project';
import { computeNextReferenceNumber } from '../../scripts/reference-number-utils';
import {
  importGeneralDocumentFileFromDesign,
  uploadGeneralDocumentFile,
} from '../../models/api/filesystem';
import { MULTI_PART_FILE_SIZE } from '../../scripts/constants';
import { reloadDocuments } from '../../features/documents/actions';
import { useHistory } from 'react-router-dom';
import { getDocumentsType } from '../../features/documents/selectors';
import { getUserState } from '../../features/user/selectors';
import CircularLoader from '../loader/CircularLoader';
import { useAppUtilsContext } from '../AppUtilsContext';
import { SubmittalToCreate } from '../dashboard/SelectSubmittalsDialog';

interface AddPackageDialogProps {
  dialogOpen: boolean;
  handleClose: () => void;
  anchor: any;
  selected: string[];
  isPlaceholder?: boolean;
  /**
   * What the selected submittals are, for the dialog to name back. Passed where the caller picked
   * them somewhere the user can no longer see -- the dashboard's picker closes behind this dialog,
   * unlike the log page, where the ticked rows stay on screen underneath.
   */
  selectedSummaries?: { id: string; label: string }[];
  /**
   * Submittals to create with the package and add to it, alongside `selected`. Passed where a
   * ticked requirement's own submittal cannot go into a package -- it is in one already, was
   * withdrawn, or has moved past the point a package is built from -- so the package takes a fresh
   * submittal numbered into the same requirement instead. Created here rather than by the picker
   * so that abandoning this dialog leaves no submittals behind.
   */
  submittalsToCreate?: SubmittalToCreate[];
}

const useStyles = makeStyles({
  popover: {
    '.rootElement > *': {
      maxHeight: '5000px',
    },
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    height: '80px',
    padding: '8px 16px 8px 16px',
    borderBottom: '1px solid #EDECEC',
  },
  title: {
    marginTop: '3px',
    marginLeft: '6px',
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '26px',
    lineHeight: '30px',
    textAlign: 'left',
    color: '#0947B9',
    textTransform: 'uppercase',
  },
  rootIconButton: {
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  contentFlow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'left',
  },
  section: {
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'left',
  },
  checkbox: {
    padding: 3,
  },
  inputLabel: {
    marginTop: 12,
  },
  /** The submittals this package was built from, named back before anything is asked about it. */
  selectedSubmittals: {
    marginBottom: 12,
    padding: '8px 12px',
    background: '#F9F9F9',
    borderRadius: 4,
    maxHeight: 160,
    overflowY: 'auto',
  },
  selectedSubmittalsHeading: {
    color: '#0947B9',
    fontWeight: 500,
    marginBottom: 4,
  },
  selectedRow: {
    color: '#464546', // Gray 700
  },
  status: {
    marginBottom: 4,
  },
  inline: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    paddingTop: 16,
  },
  calendar: {
    maxWidth: 225,
    paddingTop: 4,
    paddingBottom: 4,
  },
  dueDate: {
    paddingTop: 8,
  },
  button: {
    width: 115,
  },
  field: {
    marginTop: 8,
    marginBottom: 8,
    padding: 0,
  },
  selectList: {
    maxHeight: 400,
  },
});

export default function AddPackageDialog(props: AddPackageDialogProps) {
  const classes = useStyles();
  const {
    dialogOpen,
    handleClose,
    anchor,
    selected,
    isPlaceholder = false,
    selectedSummaries,
    submittalsToCreate,
  } = props;

  const dispatch = useDispatch();
  const history = useHistory();
  const selectedProject = useSelector(getProjectState);
  const docType = useSelector(getDocumentsType);
  const user = useSelector(getUserState);
  const companies = useSelector(getCompaniesState);
  const shouldBlockNavigation = useSelector(getNavigationState);

  const {
    documentTemplates: { getTemplateId },
  } = useAppUtilsContext();

  const [inputPackageNumber, setInputPackageNumber] = useState<string>();
  const [inputDescription, setInputDescription] = useState<string>();
  const [inputArchitect, setInputArchitect] = useState(
    selectedProject?.architectAdministratorUserId || undefined,
  );
  // [CL174] should be nullable?
  const [inputContractor, setInputContractor] = useState(
    selectedProject?.contractorAdministratorUserId || undefined,
  );

  const [fileUploadDialogOpen, setFileUploadDialogOpen] = useState(false);
  const [file, setFile] = useState<File | FileNode>();

  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('Updating document...');

  const [assignToType, setAssignToType] = useState('user');
  const [subcontractorEmail, setSubcontractorEmail] = useState<string>();

  const [managePermissionsDialogOpen, setManagePermissionsDialogOpen] = useState(false);
  const [associatedUsers, setAssociatedUsers] = useState<string[]>([]);
  const [associatedGroups, setAssociatedGroups] = useState<string[]>([]);

  const [editArchitect, setEditArchitect] = useState(false);
  const [editContractor, setEditContractor] = useState(false);
  const contractorUser = getProjectUsers(selectedProject, false).find(
    (pUser) => pUser.userId === inputContractor,
  )?.user;
  const architectUser = getProjectUsers(selectedProject, false).find(
    (pUser) => pUser.userId === inputArchitect,
  )?.user;

  const handleUploadProgress = (event: any) => {
    setUploadProgress(Math.round((100 * event.loaded) / event.total));
  };

  const getPackageDocumentType = () => {
    if (docType === DocumentTemplateType.AsBuilt) return DocumentTemplateType.AsBuiltPackages;
    if (docType === DocumentTemplateType.CloseoutSubmittals)
      return DocumentTemplateType.CloseoutSubmittalPackages;
    return DocumentTemplateType.SubmittalPackages;
  };

  // Suggest the next Package # — one past the highest number already used by a package of
  // this type on the project — the same numbering rule the document creation dialog uses.
  // Only ever fills an empty field, so a number typed while the packages load survives.
  useEffect(() => {
    if (!dialogOpen || !selectedProject || !docType) {
      if (!dialogOpen) setInputPackageNumber(undefined);
      return;
    }
    let stillCurrent = true;
    getDocumentsByProjectIdAndType(selectedProject.id, getPackageDocumentType())
      .then((packages) => {
        if (!stillCurrent) return;
        const next = computeNextReferenceNumber(packages.map((p) => p.referenceNumber));
        setInputPackageNumber((current) => current || next);
      })
      .catch(() => {});
    return () => {
      stillCurrent = false;
    };
  }, [dialogOpen, docType, selectedProject?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    if (!inputPackageNumber || !inputDescription) return;
    e.preventDefault();
    setIsLoading(true);
    const packageDocumentType = getPackageDocumentType();
    if (selected.length + (submittalsToCreate?.length || 0) <= 0) {
      return;
    }
    try {
      dispatch(blockNavigation());
      setLoadingText('Creating document...');
      const documentTemplateId = getTemplateId(packageDocumentType);
      const document: IInsertionDocumentWithGraph = {
        documentTemplateId,
        creatorUserId: user.id,
        lastUpdatedByUserId: user.id,
        projectId: selectedProject!.id,
        referenceNumber: inputPackageNumber,
        title: inputDescription,
        architectUserId: inputArchitect,
        generalContractorUserId: inputContractor,
      };

      const newPackage = await insertDocument(document, {
        followerUserIds: associatedUsers,
        userGroupIds: associatedGroups,
        skipInitialStatus: !isPlaceholder,
      });

      if (file) {
        const category = isPlaceholder
          ? FileCategoryType.DocumentBase
          : FileCategoryType.DocumentGeneralContractorApproved;

        if ((file as FileNode).relativeKey !== undefined) {
          setLoadingText(`Uploading ${(file as FileNode).relativeKey}...`);
          await importGeneralDocumentFileFromDesign(newPackage.id, category, {
            ownerId: (file as FileNode).ownerId,
            fullKey: (file as FileNode).fullKey,
            fullFileName: (file as FileNode).relativeKey!,
            projectId: (file as FileNode).projectId,
          });
        } else {
          setLoadingText(`Uploading ${(file as File).name}...`);
          await uploadGeneralDocumentFile(
            newPackage.id,
            {
              fullFileName: (file as File).name,
              fileType: category,
              useMultiPartUpload: (file as File).size > MULTI_PART_FILE_SIZE,
            },
            file as File,
            handleUploadProgress,
          );
        }
      }

      // A requirement whose own submittal cannot be packaged is met by a fresh submittal against
      // the same requirement, created here so an abandoned package leaves none behind. It starts
      // in the initial status -- awaiting submission -- which is the state a package is built from.
      let createdSubmittalIds: string[] = [];
      if (submittalsToCreate?.length) {
        setLoadingText('Creating submittals...');
        createdSubmittalIds = await Promise.all(
          submittalsToCreate.map(async (submittal) => {
            const created = await insertDocument(
              {
                documentTemplateId: getTemplateId(docType!),
                creatorUserId: user.id,
                lastUpdatedByUserId: user.id,
                projectId: selectedProject!.id,
                title: submittal.title,
                submittalSection: submittal.submittalSection,
                submittalParagraphNumber: submittal.submittalParagraphNumber,
                submittalSubparagraphNumber: submittal.submittalSubparagraphNumber,
              },
              { submittalSectionDescription: submittal.submittalSectionDescription },
            );
            return created.id;
          }),
        );
      }

      setLoadingText('Adding documents to package...');

      await Promise.all(
        [...selected, ...createdSubmittalIds].map((docId) =>
          addDocumentToPackage(docId, newPackage.id),
        ),
      );

      dispatch(allowNavigation());
      dispatch(reloadDocuments());
      history.push(
        `/main/projects/${newPackage.projectId}/documents/${documentTypeToUrl[docType!]}/${
          newPackage.id
        }`,
      );
    } finally {
      dispatch(allowNavigation());
      setIsLoading(false);
    }
  };

  return (
    <MuiPickersUtilsProvider utils={DayjsUtils}>
      <Popover
        className={classes.popover}
        open={dialogOpen}
        onClose={() => {
          if (isLoading || shouldBlockNavigation) return;
          setEditArchitect(false);
          setEditContractor(false);
          handleClose();
        }}
        anchorEl={anchor}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <form className={classes.contentFlow} onSubmit={handleSubmit}>
          <Box className={classes.section}>
            {selectedSummaries?.length ? (
              <Box className={classes.selectedSubmittals}>
                <Typography className={classes.selectedSubmittalsHeading}>
                  {`Submittals in this package (${selectedSummaries.length})`}
                </Typography>
                {selectedSummaries.map((submittal) => (
                  <Typography key={submittal.id} variant="body2" className={classes.selectedRow}>
                    {submittal.label}
                  </Typography>
                ))}
              </Box>
            ) : null}
            <TextField
              variant="outlined"
              margin="none"
              size="small"
              fullWidth
              label="Package #"
              // `?? ''` keeps the field controlled from the first render: MUI v4 latches that
              // decision on mount, and an initially undefined value leaves the label unshrunk
              // (overlapping the number) when the suggestion fills it in programmatically.
              value={inputPackageNumber ?? ''}
              className={classes.field}
              onChange={(e: any) => setInputPackageNumber(e.target.value)}
            />
            <TextField
              variant="outlined"
              margin="none"
              size="small"
              fullWidth
              multiline
              rows={5}
              label="Description"
              value={inputDescription}
              className={classes.field}
              onChange={(e: any) => setInputDescription(e.target.value)}
            />
            {/*<RadioGroup*/}
            {/*  row*/}
            {/*  value={assignToType}*/}
            {/*  onChange={(event, value) => setAssignToType(value)}*/}
            {/*  style={{ marginBottom: 0, paddingBottom: 0 }}*/}
            {/*>*/}
            {/*  <FormControlLabel value="user" control={<Radio color="primary" />} label="User" />*/}
            {/*  <FormControlLabel*/}
            {/*    value="subcontractor"*/}
            {/*    control={<Radio color="primary" />}*/}
            {/*    label="Subcontractor"*/}
            {/*  />*/}
            {/*</RadioGroup>*/}
            {assignToType === 'user' ? (
              <>
                {editContractor || !inputContractor ? (
                  <TextField
                    select
                    variant="outlined"
                    label="Assign Contractor"
                    fullWidth
                    value={inputContractor}
                    onChange={(e) => setInputContractor(e.target.value)}
                    style={{ marginBottom: 8, marginTop: 8 }}
                  >
                    {selectedProject ? (
                      getProjectUsers(selectedProject, false).map((item) => {
                        return (
                          <MenuItem key={item.id} value={item.user!.id}>
                            <AssignedUserText
                              name={item.user!.name}
                              company={
                                item.user?.company?.name ||
                                companies.find((c) => c.id === item.user?.companyId)?.name
                              }
                            />
                          </MenuItem>
                        );
                      })
                    ) : (
                      <MenuItem value="">No selected project</MenuItem>
                    )}
                  </TextField>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography style={{ marginBottom: 8, marginTop: 8 }}>
                      <span style={{ fontWeight: 500 }}>Assigned Contractor:</span>{' '}
                      {contractorUser?.name}
                    </Typography>
                    <Button variant="text" color="primary" onClick={() => setEditContractor(true)}>
                      EDIT
                    </Button>
                  </div>
                )}
                {editArchitect || !inputArchitect ? (
                  <TextField
                    select
                    variant="outlined"
                    label="Assign Architect"
                    fullWidth
                    value={inputArchitect}
                    onChange={(e) => setInputArchitect(e.target.value)}
                    style={{ marginBottom: 8, marginTop: 8 }}
                  >
                    {selectedProject ? (
                      getProjectUsers(selectedProject, false).map((item) => {
                        return (
                          <MenuItem key={item.id} value={item.userId}>
                            <AssignedUserText
                              name={item.user!.name}
                              company={
                                item.user?.company?.name ||
                                companies.find((c) => c.id === item.user?.companyId)?.name
                              }
                            />
                          </MenuItem>
                        );
                      })
                    ) : (
                      <MenuItem value="">No selected project</MenuItem>
                    )}
                  </TextField>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography style={{ marginBottom: 8, marginTop: 8 }}>
                      <span style={{ fontWeight: 500 }}>Assigned Architect:</span>{' '}
                      {architectUser?.name}
                    </Typography>
                    <Button variant="text" color="primary" onClick={() => setEditArchitect(true)}>
                      EDIT
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <TextField
                variant="outlined"
                margin="none"
                size="small"
                fullWidth
                id="project-number"
                label="Subcontractor's email"
                name="Title"
                value={subcontractorEmail}
                className={classes.field}
                onChange={(e: any) => setSubcontractorEmail(e.target.value)}
              />
            )}
            <div style={{ fontSize: '15px' }}>FILES: {!file && 'None'}</div>
            {file && (
              <div
                key={generateUniqueId()}
                style={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '2px',
                }}
              >
                <div
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '80%',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {(file as File).name || (file as FileNode).relativeKey}
                </div>
                <Clear
                  style={{ cursor: 'pointer' }}
                  onClick={() => setFile(undefined)}
                  fill="#949494"
                />
              </div>
            )}
            <Button
              variant="outlined"
              color="primary"
              style={{ height: '32px', width: '100%', marginTop: 8 }}
              onClick={() => setFileUploadDialogOpen(true)}
              startIcon={<Publish fill="#2C69D6" />}
            >
              {/* A package that is not a placeholder is the file, so the file is required -- the
                  same asterisk AddDocumentDialog puts on its own Upload button. */}
              UPLOAD{!isPlaceholder && '*'}
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={() => setManagePermissionsDialogOpen(true)}
              fullWidth
              style={{ height: '100%', marginTop: 12, marginBottom: 8, alignSelf: 'center' }}
            >
              Define Notification List
            </Button>
            <InlineNotificationList
              associatedUsers={associatedUsers}
              associatedGroups={associatedGroups}
            />
            {isLoading ? (
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  marginTop: 8,
                }}
              >
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  {loadingText}
                </Typography>
                {loadingText.toLowerCase().includes('uploading') ? (
                  <>
                    <Typography style={{ marginTop: 4 }}>{uploadProgress}%</Typography>
                    <LinearProgress
                      variant="determinate"
                      value={uploadProgress}
                      style={{ height: 6, width: '100%', marginTop: 4 }}
                    />
                  </>
                ) : (
                  <CircularLoader style={{ margin: '4px 0px 4px 0px' }} />
                )}
              </div>
            ) : (
              <div className={classes.inline}>
                <CancelButton
                  onClick={handleClose}
                  variant="outlined"
                  className={classes.button}
                  style={{ color: '#949494' }}
                >
                  Cancel
                </CancelButton>

                <Button
                  color="primary"
                  type="submit"
                  disabled={!inputDescription || !inputPackageNumber || (!isPlaceholder && !file)}
                  className={classes.button}
                  variant="contained"
                >
                  Create
                </Button>
              </div>
            )}
          </Box>
        </form>
      </Popover>
      <FileUploadDialog
        open={fileUploadDialogOpen}
        handleClose={() => setFileUploadDialogOpen(false)}
        title="Upload File"
        addFile={(file) => setFile(file as File | FileNode)}
        file={file}
        canSubmit={!!file}
        removeFile={() => setFile(undefined)}
        disableComments
      />
      <ManagePermissionsDialog
        dialogOpen={managePermissionsDialogOpen}
        closeDialog={() => {
          window.dispatchEvent(new CustomEvent('resize'));
          setManagePermissionsDialogOpen(false);
        }}
        type={ManagePermissionsDialogType.AssociatedUsers}
        associatedUsers={associatedUsers}
        associatedGroups={associatedGroups}
        setAssociatedUsers={setAssociatedUsers}
        setAssociatedGroups={setAssociatedGroups}
      />
    </MuiPickersUtilsProvider>
  );
}
